import { installHistoryMemory, makeQueryClient } from '@croissants/ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { LocaleProvider } from './i18n/index';
import { AuthProvider } from './lib/index';
import './styles.css';

/**
 * Before the first render: Back - the arrow and the browser's own - returns an
 * operator to the view they left, filters and scroll included (history-memory).
 */
installHistoryMemory();

/**
 * Entry point. TanStack Query owns server state; component state stays local.
 *
 * Provider order is deliberate: the locale is outermost because the sign-in gate
 * and the configuration errors it renders are themselves translated, and the
 * session sits above the API client because the client reads its credentials from
 * the session on every request.
 */
const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

/**
 * The platform's one query client (`makeQueryClient`, @croissants/ui, F6),
 * unchanged: its retry predicate and network mode are what stop a failure
 * parking at `fetchStatus: 'paused'` behind a skeleton that never resolves, and
 * it never retries a write - a silent second attempt would hide a failure the
 * operator has to see (PRD PAY-006).
 */
const queryClient = makeQueryClient();

createRoot(container).render(
  <StrictMode>
    <LocaleProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <App />
        </AuthProvider>
      </QueryClientProvider>
    </LocaleProvider>
  </StrictMode>,
);
